import { createFileRoute } from '@tanstack/react-router';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { UserProfile } from '@clerk/clerk-react';
import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { PilotStatus } from '~/components/ui/layout/page/PilotStatus';
import { BasicPage } from '~/components/ui/layout/page/basic/BasicPage';
import { HeaderContainer } from '~/components/ui/layout/page/HeaderContainer';
import { Breadcrumb } from '~/components/ui/layout/navigation/breadcrumb/Breadcrumb';
import { PageContent } from '~/components/ui/layout/page/PageContent';
import { authAtom, jotaiStore } from '~/atoms/authAtom';

export const Route = createFileRoute('/_authLayout/settings-member')({
  component: SettingsMemberComponent,
});

function SettingsMemberComponent() {
  const [feedback, setFeedback] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const auth = useAuth();
  const MAX_FEEDBACK_LENGTH = 1000;

  const handleFeedbackSubmit = async () => {
    if (!feedback.trim()) return;

    try {
      setIsSubmittingFeedback(true);

      const authState = jotaiStore.get(authAtom);
      const token = await authState?.auth.getToken({
        template: 'banqora_template',
      });

      if (!token) {
        throw new Error('Failed to get authentication token');
      }

      const response = await fetch(
        `${process.env.REACT_BANQORA_PORTFOLIO_ANALYSIS_API_URL}api/v1/portfolio-analysis/feedback`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message: feedback,
            userId: auth.userId,
            orgId: auth.orgId,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`,
        );
      }

      const result = await response.json();
      console.log('Feedback submitted successfully:', result);

      setFeedback('');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <BasicPage title="Settings">
      <HeaderContainer>
        <Breadcrumb items={[{ name: 'Settings' }]} />
      </HeaderContainer>

      <PageContent>
        <div
          style={{
            backgroundColor: 'var(--bq-surface-card)',
            minHeight: 'calc(100vh - 200px)',
            padding: '24px',
            borderRadius: '8px',
          }}
        >
          {/* Disclaimer Section */}
          <div style={{ marginBottom: '48px' }}>
            <h4
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--bq-text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '12px',
                marginTop: 0,
              }}
            >
              Disclaimer
            </h4>
            <p
              style={{
                fontSize: '14px',
                color: 'var(--bq-text-primary)',
                lineHeight: '1.6',
                margin: 0,
              }}
            >
              This application uses publicly available news and market data -
              Bloomberg connectivity and internal system integration is
              available. Metrics may not be in line with internal data. Not
              investment advice.
            </p>
          </div>

          {/* Pilot Status Section */}
          <div style={{ marginBottom: '48px' }}>
            <h4
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--bq-text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '12px',
                marginTop: 0,
              }}
            >
              Pilot Status
            </h4>
            <PilotStatus />
          </div>

          {/* Feedback Section */}
          <div style={{ marginBottom: '48px' }}>
            <h4
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--bq-text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '12px',
                marginTop: 0,
              }}
            >
              Send Feedback
            </h4>
            <div
              style={{
                backgroundColor: 'var(--bq-surface-muted)',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid var(--bq-border-muted)',
              }}
            >
              <div style={{ marginBottom: '16px' }}>
                <p
                  style={{
                    fontSize: '14px',
                    color: 'var(--bq-text-secondary)',
                    marginBottom: '12px',
                  }}
                >
                  We value your feedback! Please share your thoughts about the
                  pilot experience to help us improve.
                </p>
                <InputTextarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Share your thoughts about the pilot experience..."
                  rows={4}
                  style={{
                    width: '100%',
                    resize: 'none',
                    backgroundColor: 'var(--bq-surface-elevated)',
                    border: '1px solid var(--bq-border-muted)',
                    color: 'var(--bq-text-primary)',
                  }}
                  maxLength={MAX_FEEDBACK_LENGTH}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '8px',
                  }}
                >
                  <small
                    style={{
                      fontSize: '12px',
                      color:
                        feedback.length > MAX_FEEDBACK_LENGTH
                          ? 'var(--bq-color-danger)'
                          : 'var(--bq-text-tertiary)',
                    }}
                  >
                    {feedback.length > MAX_FEEDBACK_LENGTH
                      ? `Character limit exceeded (${feedback.length}/${MAX_FEEDBACK_LENGTH})`
                      : `${feedback.length}/${MAX_FEEDBACK_LENGTH} characters`}
                  </small>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  label="Submit Feedback"
                  icon={
                    isSubmittingFeedback
                      ? 'pi pi-spin pi-spinner'
                      : 'pi pi-send'
                  }
                  onClick={handleFeedbackSubmit}
                  loading={isSubmittingFeedback}
                  disabled={
                    !feedback.trim() ||
                    isSubmittingFeedback ||
                    feedback.length > MAX_FEEDBACK_LENGTH
                  }
                />
              </div>
            </div>
          </div>

          {/* User Management Section */}
          <div style={{ marginBottom: '48px' }}>
            <h4
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--bq-text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '16px',
                marginTop: 0,
              }}
            >
              User Management
            </h4>
            <div
              style={{
                backgroundColor: 'var(--bq-surface-muted)',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid var(--bq-border-muted)',
              }}
            >
              <UserProfile
                appearance={{
                  baseTheme: undefined,
                  variables: {
                    colorBackground: 'var(--bq-surface-card)',
                    colorInputBackground: 'var(--bq-surface-elevated)',
                    colorInputText: 'var(--bq-text-primary)',
                    colorText: 'var(--bq-text-primary)',
                    colorTextSecondary: 'var(--bq-text-secondary)',
                    colorPrimary: 'var(--primary-color)',
                    colorDanger: 'var(--bq-color-danger)',
                    colorSuccess: 'var(--bq-color-success)',
                    colorWarning: 'var(--bq-color-warning)',
                    borderRadius: '6px',
                  },
                  elements: {
                    rootBox: {
                      width: '100%',
                    },
                    card: {
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      border: 'none',
                    },
                    navbar: {
                      backgroundColor: 'var(--bq-surface-muted)',
                    },
                    navbarButton: {
                      color: 'var(--bq-text-secondary)',
                    },
                    navbarButtonActive: {
                      color: 'var(--bq-text-primary)',
                      backgroundColor: 'var(--bq-surface-elevated)',
                    },
                    formButtonPrimary: {
                      backgroundColor: 'var(--primary-color)',
                      color: 'var(--bq-button-text)',
                    },
                    formFieldInput: {
                      backgroundColor: 'var(--bq-surface-elevated)',
                      borderColor: 'var(--bq-border-muted)',
                      color: 'var(--bq-text-primary)',
                    },
                    formFieldLabel: {
                      color: 'var(--bq-text-primary)',
                    },
                    dividerLine: {
                      backgroundColor: 'var(--bq-border-muted)',
                    },
                    dividerText: {
                      color: 'var(--bq-text-secondary)',
                    },
                    headerTitle: {
                      color: 'var(--bq-text-primary)',
                    },
                    headerSubtitle: {
                      color: 'var(--bq-text-secondary)',
                    },
                    profileSectionTitleText: {
                      color: 'var(--bq-text-primary)',
                    },
                    profileSectionContent: {
                      color: 'var(--bq-text-primary)',
                    },
                    accordionTriggerButton: {
                      color: 'var(--bq-text-primary)',
                      backgroundColor: 'var(--bq-surface-muted)',
                    },
                    badge: {
                      backgroundColor: 'var(--bq-surface-elevated)',
                      color: 'var(--bq-text-primary)',
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      </PageContent>
    </BasicPage>
  );
}
